import { Navigate, useLocation, useRoutes } from "react-router-dom";
import { ROUTES } from "../lib/constants.js";
import PageNotFound from "../pages/PageNotFound.js";
import HostBehaviourPage from "../pages/behaviour/HostBehaviourPage.js";
import Home from "../pages/home/Home.js";
import HostBackground from "../pages/host/HostBackground.js";
import HostEvents from "../pages/host/HostEvents.js";
import HostStats from "../pages/host/HostStats.js";
import HostsList from "../pages/hosts/HostsList.js";
import VisibleHostsList from "../pages/hosts/VisibleHostsList.js";
import LoginPage from "../pages/login/LoginPage.js";
import HostMaintenancePage from "../pages/maintenance/HostMaintenancePage.js";
import Plots from "../pages/plots/Plots.js";
import CharacterPage from "../pages/plots/characters/CharacterPage.js";
import CharactersList from "../pages/plots/characters/CharactersList.js";
import EventsList from "../pages/plots/events/EventsList.js";
import PlotPage from "../pages/plots/plots/PlotPage.js";
import PlotsList from "../pages/plots/plots/PlotsList.js";
import RunPage from "../pages/runs/RunPage.js";
import RunsList from "../pages/runs/RunsList.js";
import Security from "../pages/security/Security.js";
import EmployeeLogPage from "../pages/security/components/EmployeeLogPage.js";
import EmployeesList from "../pages/security/components/EmployeesList.js";
import HostLogPage from "../pages/security/components/HostLogPage.js";
import { useUser } from "./UserContext.js";
import AdminRoute from "./protected-routes/AdminRoute.js";
import EmployeeRoute from "./protected-routes/EmployeeRoute.js";
import HostRoute from "./protected-routes/HostRoute.js";

export default function Routes({ locales }) {
  const { isLoggedIn, runId } = useUser();
  const location = useLocation();

  return useRoutes([
    {
      path: "/",
      element: isLoggedIn ? (
        <Navigate to={ROUTES.HOME.replace(":runId", runId)} replace />
      ) : (
        <PageNotFound locales={locales} replace />
      ),
    },
    {
      path: ROUTES.BASE,
      element: isLoggedIn ? (
        <Navigate to={ROUTES.HOME.replace(":runId", runId)} replace />
      ) : (
        <LoginPage locales={locales} />
      ),
    },
    {
      path: ROUTES.HOME,
      element: isLoggedIn ? (
        <Home locales={locales} />
      ) : (
        <Navigate to={"/"} replace />
      ),
    },
    {
      path: ROUTES.HOST_BACKGROUND,
      element: (
        <HostRoute>
          <HostBackground locales={locales} />
        </HostRoute>
      ),
    },
    {
      path: ROUTES.HOST_STATS,
      element: (
        <HostRoute>
          <HostStats locales={locales} />
        </HostRoute>
      ),
    },
    {
      path: ROUTES.HOST_EVENTS,
      element: (
        <HostRoute>
          <HostEvents locales={locales} />
        </HostRoute>
      ),
    },
    {
      path: ROUTES.HOST_LOG,
      element: (
        <HostRoute>
          <HostLogPage locales={locales} />
        </HostRoute>
      ),
    },
    {
      path: ROUTES.PLOTS.BASE,
      element: (
        <EmployeeRoute>
          <Plots locales={locales} />
        </EmployeeRoute>
      ),
      children: [
        {
          path: "",
          element: <Navigate to="plots" replace />,
        },
        {
          path: ROUTES.PLOTS.PLOTS.BASE,
          element: <PlotsList locales={locales} />,
        },
        {
          path: ROUTES.PLOTS.PLOTS.DETAIL,
          element: <PlotPage locales={locales} />,
        },
        {
          path: ROUTES.PLOTS.PLOTS.CREATE,
          element: <PlotPage locales={locales} isCreating />,
        },
        {
          path: ROUTES.PLOTS.CHARACTERS.BASE,
          element: <CharactersList locales={locales} />,
        },
        {
          path: ROUTES.PLOTS.CHARACTERS.DETAIL,
          element: <CharacterPage locales={locales} />,
        },
        {
          path: ROUTES.PLOTS.CHARACTERS.CREATE,
          element: <CharacterPage locales={locales} isCreating />,
        },
        {
          path: ROUTES.PLOTS.EVENTS.BASE,
          element: <EventsList locales={locales} />,
        },
      ],
    },
    {
      path: ROUTES.BEHAVIOUR.BASE,
      element: (
        <EmployeeRoute>
          <HostsList locales={locales} />
        </EmployeeRoute>
      ),
    },
    {
      path: ROUTES.BEHAVIOUR.DETAIL,
      element: (
        <EmployeeRoute>
          <HostBehaviourPage locales={locales} />
        </EmployeeRoute>
      ),
    },
    {
      path: ROUTES.MAINTENANCE.BASE,
      element: (
        <EmployeeRoute>
          <HostsList locales={locales} />
        </EmployeeRoute>
      ),
    },
    {
      path: ROUTES.MAINTENANCE.DETAIL,
      element: (
        <EmployeeRoute>
          <HostMaintenancePage locales={locales} />
        </EmployeeRoute>
      ),
    },
    {
      path: ROUTES.SECURITY.BASE,
      element: (
        <EmployeeRoute>
          <Security locales={locales} />
        </EmployeeRoute>
      ),
      children: [
        {
          path: "",
          element: <Navigate to={ROUTES.SECURITY.HOSTS.BASE} replace />,
        },
        {
          path: ROUTES.SECURITY.HOSTS.BASE,
          element: <HostsList locales={locales} />,
        },
        {
          path: ROUTES.SECURITY.HOSTS.DETAIL,
          element: <HostLogPage locales={locales} />,
        },
        {
          path: ROUTES.SECURITY.EMPLOYEES.BASE,
          element: <EmployeesList locales={locales} />,
        },
        {
          path: ROUTES.SECURITY.EMPLOYEES.DETAIL,
          element: <EmployeeLogPage locales={locales} />,
        },
      ],
    },
    {
      path: ROUTES.RUNS.BASE,
      element: (
        <AdminRoute>
          <RunsList locales={locales} />
        </AdminRoute>
      ),
    },
    {
      path: ROUTES.RUNS.DETAIL,
      element: (
        <AdminRoute>
          {/* Key prop forces component remounting when navigating to same route with different ID */}
          <RunPage locales={locales} key={location.state?.key} />
        </AdminRoute>
      ),
    },
    {
      path: ROUTES.HOSTS.BASE,
      element: (
        <AdminRoute>
          <VisibleHostsList locales={locales} />
        </AdminRoute>
      ),
    },
  ]);
}
